type PredicateColumn = string | null;

type PredicateValidator<T> = (predicate: unknown) => predicate is T;

export type PredicateParserType<T> = {
  parsePredicate: (predicate: PredicateColumn) => T | null;
  unparsePredicate: (predicateObj: T) => PredicateColumn;
};

const INVALID_PREDICATE_JSON_ERROR = "Predicate JSON does not match the expected shape";

// ----------------------------------------------------------------------------
//                              Helper Functions
// ----------------------------------------------------------------------------

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const parseJsonPredicate = <T>(
  predicate: PredicateColumn,
  validatePredicate: PredicateValidator<T>,
): T | null => {
  if (!predicate) {
    return null;
  }

  let parsedPredicate: unknown;
  try {
    parsedPredicate = JSON.parse(predicate);
  } catch {
    throw new Error(INVALID_PREDICATE_JSON_ERROR);
  }

  if (!validatePredicate(parsedPredicate)) {
    throw new Error(INVALID_PREDICATE_JSON_ERROR);
  }

  return parsedPredicate;
};

const unparsePredicate = <T>(predicateObj: T): PredicateColumn => {
  if (!predicateObj) {
    return null;
  }

  return JSON.stringify(predicateObj);
};

export const predicateParser = <T>(
  validatePredicate: PredicateValidator<T>,
): PredicateParserType<T> => {
  return {
    parsePredicate: (predicate) => parseJsonPredicate(predicate, validatePredicate),
    unparsePredicate: unparsePredicate<T>,
  };
};

// ----------------------------------------------------------------------------
//                        Null Predicate Parser
// ----------------------------------------------------------------------------

const isNullPredicate = (predicate: unknown): predicate is null => predicate === null;

export const parseUnparseNullPredicate = predicateParser<null>(isNullPredicate);

// ----------------------------------------------------------------------------
//                        Echo Predicate Parser
// ----------------------------------------------------------------------------

type EchoPredicate = {
  answer: string;
};

const isEchoPredicate = (predicate: unknown): predicate is EchoPredicate =>
  isPlainObject(predicate) && typeof predicate.answer === "string";

export const parseUnparseEchoPredicate = predicateParser<EchoPredicate>(isEchoPredicate);

// ----------------------------------------------------------------------------
//                     Echo Predicate Number Parser
// ----------------------------------------------------------------------------

type EchoPredicateNumber = {
  answer: number;
};

const isEchoPredicateNumber = (predicate: unknown): predicate is EchoPredicateNumber =>
  isPlainObject(predicate) && typeof predicate.answer === "number";

export const parseUnparseEchoPredicateNumber = predicateParser<EchoPredicateNumber>(isEchoPredicateNumber);
